/**
 * OaSIS Explorer Frontend - Usage Strip Component
 * Task 30.10: Navbar Redesign
 * 
 * Slim usage indicator strip (~28px) with vertical bar indicators.
 * Shows for guests and free users, hidden for Pro users.
 * Follows STYLE_GUIDE.md patterns for dark mode.
 */

import React from 'react';
import { Zap, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../contexts';
import { useSearchUsage } from '../../hooks/useSearchUsage';

/**
 * Calculate hours until reset from ISO timestamp
 */
function getHoursUntilReset(resetAt: string | null): number {
  if (!resetAt) return 12;
  const resetTime = new Date(resetAt).getTime();
  return Math.max(0, Math.ceil((resetTime - Date.now()) / (1000 * 60 * 60)));
}

/**
 * Usage Strip Component
 * Displays vertical bar indicators, remaining count, reset time, and upgrade CTA
 * Height: ~28px (py-1.5)
 * Supports light and dark modes
 */
export const UsageStrip: React.FC = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const { plan, searchesUsed, dailyLimit, resetAt, isLoading, isTrialing } = useSearchUsage();

  // Don't show for Pro users, trialing users, or while loading
  if (plan === 'pro' || isTrialing || isLoading) {
    return null;
  }

  const hoursUntilReset = getHoursUntilReset(resetAt);
  const remaining = dailyLimit - searchesUsed;
  const isLow = remaining <= 2 && remaining > 0;
  const isExhausted = remaining <= 0;

  // ARIA label for accessibility
  const ariaLabel = `${searchesUsed} of ${dailyLimit} daily searches used, resets in ${hoursUntilReset} hours`;

  const handleUpgradeClick = () => {
    navigate('/profile#subscription');
  };

  const handleSignUpClick = () => {
    const currentPath = window.location.pathname + window.location.search;
    navigate(`/signup?redirect=${encodeURIComponent(currentPath)}`);
  };

  // Get bar color based on state
  const getUsedBarColor = () => {
    if (isExhausted) return 'bg-red-500';
    if (isLow) return 'bg-amber-500';
    return 'bg-blue-500';
  };

  return (
    <div
      className="bg-gray-100 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 py-1.5"
      role="status"
      aria-label={ariaLabel}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between text-xs">
          {/* Left Section: Vertical Bars + Count */}
          <div className="flex items-center gap-3">
            {/* Vertical bar indicators */}
            <div className="flex items-center gap-0.5">
              {Array.from({ length: dailyLimit }).map((_, i) => (
                <div
                  key={i}
                  className={`w-1 h-3 rounded-sm transition-colors duration-300 ${
                    i < searchesUsed ? getUsedBarColor() : 'bg-gray-300 dark:bg-gray-700'
                  }`}
                />
              ))}
            </div>

            {/* Searches left text */}
            <span className="text-gray-600 dark:text-gray-400">
              {remaining} {remaining === 1 ? 'search' : 'searches'} left today
            </span>

            {/* Reset time - hidden on small screens */}
            <span className="text-gray-500 dark:text-gray-500 hidden sm:inline">
              {t('usage.banner.resetsIn').replace('{hours}', String(hoursUntilReset))}
            </span>
          </div>

          {/* Right Section: CTA */}
          <div>
            {plan === 'anonymous' ? (
              <button
                onClick={handleSignUpClick}
                className={`flex items-center gap-1 font-medium transition-colors ${
                  isExhausted
                    ? 'px-2.5 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-400 hover:to-orange-400'
                    : 'text-amber-400 hover:text-amber-300'
                }`}
              >
                <UserPlus className="w-3.5 h-3.5" />
                <span>{t('usage.banner.signUpFree')}</span>
              </button>
            ) : (
              <button
                onClick={handleUpgradeClick}
                className={`flex items-center gap-1 font-medium transition-colors ${
                  isExhausted
                    ? 'px-2.5 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-400 hover:to-orange-400'
                    : 'text-amber-400 hover:text-amber-300'
                }`}
              >
                <Zap className="w-3.5 h-3.5" />
                <span>{t('usage.banner.getUnlimited')}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageStrip;
